import React, { useState } from 'react';
import { 
  LayoutDashboard, Gauge, Users, Receipt, BarChart3, Bell, Settings, 
  Fuel, Building2, AlertTriangle, FileText, Zap, CreditCard, 
  ChevronRight, ChevronDown, BookOpen, HelpCircle, Search,
  Calendar, TrendingUp, Wallet, PiggyBank, ArrowLeftRight,
  UserPlus, Shield, Eye, Download, Printer, Filter,
  Clock, CheckCircle, XCircle, AlertCircle, Info,
  Plus, Minus, Edit, Trash2, RefreshCw, Home
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';

const UserGuide = ({ onClose }) => {
  const [expandedSections, setExpandedSections] = useState(['getting-started']);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Home,
      content: [
        {
          title: 'Welcome to Fuelogic',
          content: `Fuelogic is a comprehensive Fuel Station Management System designed for Indian fuel retailers. It helps you manage daily operations, track sales, monitor stock levels, handle customer credit accounts, and generate reports.

**Key Features:**
• Real-time dashboard with sales and stock monitoring
• Daily operations tracking (dip readings, totalizer readings)
• Customer credit management with detailed ledgers
• Station accounting with actual vs ideal reconciliation
• Multi-station support for pump owners with multiple outlets
• Role-based access control for staff management
• Comprehensive reporting and PDF exports`
        },
        {
          title: 'First Login',
          content: `When you first log in, you'll be prompted to change your PIN if it's your first time. 

**Login Credentials:**
• Mobile Number: Your registered mobile number
• PIN: 6-digit PIN provided by your administrator

**Security Tips:**
• Change your PIN immediately after first login
• Never share your PIN with others
• Log out when leaving the system unattended`
        },
        {
          title: 'Navigation',
          content: `The application has a sidebar navigation with the following main sections:

• **Dashboard** - Overview of your station's performance
• **Daily Operations** - Record daily readings and stock
• **Quick Actions** - Add sales, payments, expenses quickly
• **Accounts** - View station and customer ledgers
• **Customers** - Manage credit customers
• **Reports** - Generate and download reports
• **Settings** - Configure system preferences

Your access to these sections depends on your assigned role (Admin, Manager, Standard, or Viewer).`
        }
      ]
    },
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: LayoutDashboard,
      content: [
        {
          title: 'Dashboard Overview',
          content: `The Dashboard provides a real-time snapshot of your fuel station's performance.

**Top Cards:**
• **Yesterday's Revenue** - Total collections from the previous day
• **This Month Revenue** - Cumulative revenue for the current month
• **Credit Outstanding** - Total amount owed by credit customers
• **Unread Alerts** - Number of pending notifications

**Tank Levels:**
Visual representation of current fuel stock in your tanks:
• MS (Petrol) - Shows percentage and actual liters
• HSD (Diesel) - Shows percentage and actual liters
• Color coding: Green (>50%), Yellow (20-50%), Red (<20%)`
        },
        {
          title: 'Today\'s Sales & Loss',
          content: `**Today's Sales:**
Shows the quantity (in liters) of MS and HSD sold today based on totalizer readings.

**Today's Loss:**
Displays any variance between expected and actual stock:
• **OK** - No significant loss detected
• **Loss shown in red** - Actual stock is less than expected
• **Gain shown in green** - Actual stock is more than expected (rare, may indicate measurement error)

**Note:** Loss calculation requires previous day's stock entry to be recorded.`
        },
        {
          title: 'Performance Charts',
          content: `**Time Period Selection:**
Use the period selector (Week, Month, 12 Months, This Year) to change the chart data range.

**Available Charts:**
1. **Sales Trend** - Daily/weekly MS and HSD sales volume
2. **Revenue Trend** - Daily/weekly revenue in rupees
3. **Loss Trend** - Track fuel losses over time
4. **Credit Collection Trend** - Monitor credit payment collections

**Tip:** Hover over chart points to see exact values.`
        }
      ]
    },
    {
      id: 'daily-operations',
      title: 'Daily Operations',
      icon: Gauge,
      content: [
        {
          title: 'Recording Daily Stock',
          content: `Daily stock recording is essential for accurate sales calculation and loss detection.

**When to Record:**
Record daily stock at the same time each day (typically at shift change or day start/end).

**Steps:**
1. Go to Daily Operations page
2. Select the date (defaults to today)
3. Enter Dip Readings for each tank
4. Enter Totalizer Readings for each dispenser nozzle
5. Click "Save Daily Stock"

**Important:** Ensure readings are accurate as they affect:
• Sales calculations
• Loss detection
• Stock alerts`
        },
        {
          title: 'Dip Readings',
          content: `Dip readings measure the actual fuel level in your underground tanks.

**How to Enter:**
• Select the tank number
• Select fuel type (MS/HSD)
• Enter the dip reading in liters

**Conversion:**
If you measure in centimeters, use your tank's calibration chart to convert to liters.

**Tips:**
• Take readings when pumps are idle
• Allow fuel to settle after delivery before measuring
• Record temperature if significant variation exists`
        },
        {
          title: 'Totalizer Readings',
          content: `Totalizer readings are the cumulative meter readings from each dispenser nozzle.

**How to Enter:**
• Select dispenser number
• Select fuel type
• Enter the current totalizer reading

**Understanding Totalizers:**
• Totalizers are cumulative (always increasing)
• Sales = Today's Reading - Yesterday's Reading
• Each nozzle has its own totalizer

**Common Issues:**
• If reading is less than previous day, check for meter reset
• Record partial readings if dispenser was serviced`
        },
        {
          title: 'TT Readings (Tank Truck Delivery)',
          content: `Record fuel deliveries from tank trucks to update your stock.

**Information Required:**
• Date and time of delivery
• Invoice/Challan number
• MS quantity received (liters)
• HSD quantity received (liters)
• Purchase price per liter

**Impact:**
• Increases tank stock levels
• Creates station ledger entry
• Updates expected stock for loss calculation

**Verification:**
Always verify delivered quantity against invoice before accepting delivery.`
        }
      ]
    },
    {
      id: 'quick-actions',
      title: 'Quick Actions',
      icon: Zap,
      content: [
        {
          title: 'Quick Actions Overview',
          content: `Quick Actions provides fast access to common daily transactions:

• **Add Sale** - Record credit or cash sales to customers
• **Add Payment** - Record payments received from credit customers
• **Add Expense** - Record operational expenses
• **Add Withdrawal** - Record owner/partner cash withdrawals
• **Cash Request** - Record cash given to customers on request

**Access:** Click "Quick Actions" in the sidebar or use the floating action button on mobile.`
        },
        {
          title: 'Add Sale',
          content: `Record fuel sales to registered credit customers.

**Required Information:**
• Customer name (select from list)
• Fuel type (MS/HSD)
• Quantity in liters
• Unit price (auto-filled from settings)
• Discount (if any)
• Payment type: Credit or Paid

**For Credit Sales:**
• Amount is added to customer's outstanding balance
• Entry appears in customer ledger
• No entry in station ledger (until payment received)

**For Paid Sales:**
• Select payment method (Cash, Card, UPI, etc.)
• Entry appears in both customer and station ledger
• Customer balance remains unchanged`
        },
        {
          title: 'Add Payment',
          content: `Record payments received from credit customers.

**Required Information:**
• Customer name
• Amount received
• Payment method
• Date of payment
• Reference number (optional)

**Impact:**
• Reduces customer's outstanding balance
• Creates credit entry in customer ledger
• Creates credit entry in station ledger (actual column)

**Partial Payments:**
You can accept partial payments. The remaining balance will continue to show in customer's account.`
        },
        {
          title: 'Add Expense',
          content: `Record operational expenses for your fuel station.

**Expense Categories:**
• Staff Salaries
• Electricity
• Rent
• Maintenance & Repairs
• Office Supplies
• Transportation
• Marketing
• Miscellaneous

**Required Information:**
• Category
• Description
• Amount
• Date
• Receipt/Bill number (optional)

**Impact:**
• Creates debit entry in station ledger
• Appears in expense reports
• Affects profit/loss calculations`
        },
        {
          title: 'Add Withdrawal',
          content: `Record cash withdrawals by owners or partners.

**Use Cases:**
• Owner's personal drawings
• Partner profit distribution
• Emergency cash needs

**Required Information:**
• Withdrawn by (name)
• Amount
• Purpose/Reason
• Date

**Impact:**
• Creates debit entry in station ledger (both actual and ideal)
• Does not affect profit/loss (treated as capital withdrawal)
• Tracked separately in withdrawal reports`
        },
        {
          title: 'Cash Request',
          content: `Record cash given to credit customers on their request.

**Scenario:**
Sometimes credit customers request cash advances against their credit account.

**Required Information:**
• Customer name
• Amount given
• Purpose
• Date

**Impact:**
• Increases customer's outstanding balance
• Creates debit entry in customer ledger
• Creates debit entry in station ledger

**Caution:**
Cash requests increase credit exposure. Monitor customer credit limits.`
        }
      ]
    },
    {
      id: 'customers',
      title: 'Customers',
      icon: Users,
      content: [
        {
          title: 'Customer Management',
          content: `Manage your credit customers in the Customers section.

**Customer Types:**
• Fleet accounts (transport companies)
• Corporate accounts (businesses)
• Individual credit customers

**Customer Information:**
• Firm/Company name
• Contact person name
• Mobile number
• Address
• Credit limit (optional)
• Payment terms`
        },
        {
          title: 'Adding New Customer',
          content: `To add a new credit customer:

1. Go to Customers page
2. Click "Add Customer" button
3. Fill in required details:
   • Firm Name (company/business name)
   • Customer Name (contact person)
   • Mobile Number
   • Address (optional)
4. Click "Save"

**Tips:**
• Use consistent naming for easy searching
• Add mobile number for quick identification
• Set credit limits to control exposure`
        },
        {
          title: 'Customer Balance',
          content: `Each customer has an outstanding balance that shows:

**Positive Balance:** Customer owes you money (normal for credit accounts)
**Zero Balance:** All dues cleared
**Negative Balance:** You owe the customer (overpayment or advance)

**Balance Calculation:**
Balance = Total Sales + Cash Requests - Total Payments - Discounts

**Viewing Details:**
Click on a customer to see their complete transaction history in the customer ledger.`
        }
      ]
    },
    {
      id: 'accounts',
      title: 'Accounts & Ledgers',
      icon: Receipt,
      content: [
        {
          title: 'Station Ledger Overview',
          content: `The Station Ledger is your station's primary accounting record.

**6-Column System:**
Fuelogic uses a dual-tracking system:

**Actual Columns (Left):**
• Debit: Cash outflows (expenses, withdrawals)
• Credit: Cash inflows (payments received)
• Balance: Running actual cash balance

**Ideal Columns (Right):**
• Debit: Expected outflows
• Credit: Expected inflows (based on totalizer sales)
• Balance: Running ideal balance

**Why Two Sets?**
Comparing actual vs ideal helps identify:
• Collection gaps
• Unrecorded transactions
• Potential discrepancies`
        },
        {
          title: 'Entry Types in Station Ledger',
          content: `**Sale (Actual only):**
Customer payments for fuel purchases

**Daily Sales (Ideal only):**
Auto-calculated from totalizer readings

**TT Purchase (Both):**
Fuel deliveries - appears in both columns

**Withdrawal (Both):**
Owner withdrawals - mirrored in both columns

**Expense (Actual only):**
Operational costs

**Payment Received (Actual only):**
Customer payment collections

**Payment Breakdown (Actual only):**
Daily breakdown by payment method`
        },
        {
          title: 'Customer Ledger',
          content: `Each credit customer has their own ledger tracking all transactions.

**Entry Types:**
• **Fuel Purchase** - Credit sales (increases balance)
• **Payment Received** - Customer payments (decreases balance)
• **Cash Request** - Cash given to customer (increases balance)
• **Discount** - Discounts applied (decreases balance)

**Viewing Customer Ledger:**
1. Go to Accounts > Customer Ledger tab
2. Select customer from dropdown
3. Optionally filter by date range
4. View transaction history and running balance

**Export:**
Download customer ledger as PDF for sharing with customers.`
        },
        {
          title: 'Reconciliation',
          content: `Reconciliation helps verify that your records match reality.

**Types of Reconciliation:**

1. **Fuel Reconciliation:**
   • Compares stock records with actual tank levels
   • Identifies losses or gains
   • Shows variance by fuel type

2. **Cash Reconciliation:**
   • Compares expected collections with actual
   • Breaks down by payment method
   • Identifies collection gaps

3. **Sales Reconciliation:**
   • Compares totalizer sales with recorded sales
   • Identifies unrecorded transactions
   • Shows variance history

**Running Reconciliation:**
Navigate to the Reconciliation page and select the date range to analyze.`
        }
      ]
    },
    {
      id: 'reports',
      title: 'Reports',
      icon: BarChart3,
      content: [
        {
          title: 'Available Reports',
          content: `Fuelogic provides comprehensive reporting capabilities:

**Financial Reports:**
• Station Ledger Report
• Customer Ledger Reports
• Expense Report
• Profit & Loss Statement

**Operational Reports:**
• Daily Sales Report
• Stock Movement Report
• Loss Analysis Report
• Reconciliation Report

**Administrative Reports:**
• User Activity Report
• Audit Trail Report`
        },
        {
          title: 'Generating Reports',
          content: `**Steps to Generate:**
1. Go to Reports page
2. Select report type
3. Choose date range
4. Apply any filters (customer, fuel type, etc.)
5. Click "Generate"

**Export Options:**
• **View on Screen** - Interactive report with sorting/filtering
• **Download PDF** - Formatted PDF for printing or sharing
• **Export Excel** - CSV format for further analysis

**Scheduling:**
Premium users can schedule automatic report generation and email delivery.`
        },
        {
          title: 'Understanding P&L Report',
          content: `The Profit & Loss report shows your station's financial performance.

**Revenue:**
• Fuel Sales (MS + HSD)
• Other Income

**Cost of Goods Sold:**
• Fuel Purchases
• Transportation costs

**Gross Profit:**
Revenue - Cost of Goods Sold

**Operating Expenses:**
• Staff salaries
• Utilities
• Rent
• Maintenance
• Other expenses

**Net Profit:**
Gross Profit - Operating Expenses

**Note:** Profit margins in fuel retail are typically thin (2-5%). Monitor costs carefully.`
        }
      ]
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: Settings,
      content: [
        {
          title: 'Threshold Settings',
          content: `Configure alert thresholds for automatic notifications.

**Stock Thresholds:**
• MS Low Stock Alert (liters)
• HSD Low Stock Alert (liters)

**Loss Thresholds:**
• MS Loss Alert (liters)
• HSD Loss Alert (liters)

**Cash Threshold:**
• Cash Collection Alert (rupees)

**How It Works:**
When daily stock is recorded, the system checks values against thresholds and creates alerts if exceeded.`
        },
        {
          title: 'Fuel Prices',
          content: `Set your selling prices for accurate calculations.

**MS Sell Price:** Per liter price for petrol
**HSD Sell Price:** Per liter price for diesel

**Impact:**
• Used in revenue calculations
• Applied to customer sales
• Affects P&L reports

**Updating Prices:**
Update prices immediately when oil company revises rates. Historical transactions retain their original prices.`
        },
        {
          title: 'Access Control',
          content: `**Page Access:**
Control which pages each role can access:
• Admin - Full access
• Manager - Operations and reports
• Standard - Basic operations
• Viewer - Read-only dashboard

**Report Access:**
Control report viewing and download permissions:
• Can View: Yes/No
• Can Download: Yes/No
• Months Allowed: How many months of history

**Custom Payment Methods:**
Add custom payment methods beyond the defaults (Cash, Card, UPI, etc.)`
        },
        {
          title: 'UI Preferences',
          content: `**Theme Mode:**
• Light - White background
• Dark - Dark background with glass morphism
• System - Follows device preference

**Dealership Brand Theme:**
Apply your oil company's brand colors:
• Indian Oil (IOCL) - Orange theme
• Bharat Petroleum (BPCL) - Blue/Yellow theme
• Hindustan Petroleum (HPCL) - Blue/Brown theme
• Jio-bp - Green theme
• Nayara Energy - Navy/Cyan theme
• Shell - Red/Yellow theme

Select a theme to apply brand colors to headers, buttons, and accents.`
        }
      ]
    },
    {
      id: 'user-management',
      title: 'User Management',
      icon: Shield,
      content: [
        {
          title: 'Managing Users',
          content: `**User Roles:**

**Admin:**
• Full system access
• Can manage users
• Can modify settings
• Can view all reports

**Manager:**
• Operations access
• Can record transactions
• Can view reports
• Cannot modify settings

**Standard:**
• Basic operations
• Daily stock entry
• Quick actions
• Limited report access

**Viewer:**
• Read-only access
• Dashboard viewing
• Cannot make changes`
        },
        {
          title: 'Adding Users',
          content: `**To Add a New User:**
1. Go to User Management
2. Click "Add User"
3. Enter details:
   • Name
   • Mobile Number (used for login)
   • Role
4. System generates initial PIN
5. Share credentials with user
6. User must change PIN on first login

**Security:**
• Each user has unique credentials
• All actions are logged with user ID
• Admins can reset user PINs if forgotten`
        },
        {
          title: 'User Actions',
          content: `**Available Actions:**

**Edit User:**
• Change name
• Change role
• Update mobile number

**Reset PIN:**
• Generate new PIN for user
• Use when user forgets PIN
• User must change on next login

**Disable User:**
• Temporarily block access
• Use for employees on leave
• Can be re-enabled later

**Delete User:**
• Permanently remove access
• Historical records preserved
• Cannot be undone`
        }
      ]
    },
    {
      id: 'multi-station',
      title: 'Multi-Station',
      icon: Building2,
      content: [
        {
          title: 'Multi-Station Dashboard',
          content: `For owners with multiple fuel stations, the Multi-Station Dashboard provides:

**Consolidated View:**
• Total revenue across all stations
• Combined stock levels
• Aggregated alerts
• Comparative performance

**Station Cards:**
Each station shows:
• Today's sales
• Current stock levels
• Outstanding credits
• Active alerts

**Quick Actions:**
• Switch to any station's detailed view
• View comparative analytics
• Generate consolidated reports`
        },
        {
          title: 'Adding Linked Stations',
          content: `**To Link a Station:**
1. Go to Multi-Station page
2. Click "Add Station"
3. Enter station's login credentials
4. Verify and confirm linking

**Permissions:**
• View station data
• Receive alerts
• Generate reports
• Cannot modify station settings (requires direct login)

**Unlinking:**
You can unlink stations at any time. This removes access but doesn't affect the station's data.`
        },
        {
          title: 'Comparative Analytics',
          content: `Compare performance across your stations:

**Metrics Compared:**
• Daily/Monthly sales volume
• Revenue
• Loss percentage
• Credit outstanding
• Collection efficiency

**Visualization:**
• Side-by-side bar charts
• Trend comparisons
• Ranking tables

**Use Cases:**
• Identify best performing station
• Spot underperforming outlets
• Optimize inventory distribution`
        }
      ]
    },
    {
      id: 'alerts',
      title: 'Alerts & Notifications',
      icon: Bell,
      content: [
        {
          title: 'Alert Types',
          content: `**Stock Alerts:**
• Low MS stock warning
• Low HSD stock warning
• Critical stock level

**Loss Alerts:**
• MS loss exceeds threshold
• HSD loss exceeds threshold

**Collection Alerts:**
• High cash holding warning
• Pending credit collections

**System Alerts:**
• Subscription expiry warning
• System maintenance notices`
        },
        {
          title: 'Managing Alerts',
          content: `**Viewing Alerts:**
• Click bell icon in header
• Shows recent unread alerts
• Click "View All" for complete list

**Alert Actions:**
• Mark as Read
• Dismiss
• Take Action (links to relevant page)

**Alert Settings:**
Configure thresholds in Settings to control when alerts are triggered.`
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: AlertTriangle,
      content: [
        {
          title: 'Common Issues',
          content: `**Sales Not Showing:**
• Ensure previous day's stock is recorded
• Check totalizer readings are higher than previous day
• Verify date selection is correct

**Loss Calculation Wrong:**
• Verify dip readings are accurate
• Check for unrecorded TT deliveries
• Ensure no readings were skipped

**Customer Balance Incorrect:**
• Review all transactions in customer ledger
• Check for duplicate entries
• Verify payment recordings`
        },
        {
          title: 'Data Corrections',
          content: `**Correcting Entries:**
Most entries can be edited or deleted by admins within 24 hours.

**For Older Entries:**
Contact your administrator or support for corrections to historical data.

**Audit Trail:**
All corrections are logged in the audit trail for transparency.`
        },
        {
          title: 'Getting Help',
          content: `**In-App Support:**
• Click the help icon (?) for this guide
• Check tooltips on form fields

**Contact Support:**
• Email: support@fuelogic.in
• Phone: Available in business hours

**Training:**
Request training sessions for new staff through your account manager.`
        }
      ]
    }
  ];

  // Filter sections based on search
  const filteredSections = sections.map(section => ({
    ...section,
    content: section.content.filter(item =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.content.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.content.length > 0);

  const renderContent = (text) => {
    // Simple markdown-like rendering
    return text.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-semibold text-foreground mt-3 mb-1">{line.slice(2, -2)}</p>;
      }
      if (line.startsWith('• ')) {
        return <li key={i} className="ml-4 text-muted-foreground">{line.slice(2)}</li>;
      }
      if (line.includes('**')) {
        const parts = line.split('**');
        return (
          <p key={i} className="text-muted-foreground">
            {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-foreground">{part}</strong> : part)}
          </p>
        );
      }
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-muted-foreground">{line}</p>;
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">User Guide</h2>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            ✕
          </button>
        )}
      </div>

      {/* Search */}
      <div className="p-4 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search user guide..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {filteredSections.map(section => (
            <div key={section.id} className="border border-border rounded-lg overflow-hidden">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-3 bg-secondary/30 hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <section.icon className="w-4 h-4 text-primary" />
                  <span className="font-medium">{section.title}</span>
                </div>
                {expandedSections.includes(section.id) ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {/* Section Content */}
              {expandedSections.includes(section.id) && (
                <div className="p-4 space-y-4">
                  {section.content.map((item, idx) => (
                    <div key={idx} className="space-y-2">
                      <h4 className="font-medium text-primary flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        {item.title}
                      </h4>
                      <div className="pl-4 text-sm space-y-1">
                        {renderContent(item.content)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {filteredSections.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <HelpCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No results found for "{searchQuery}"</p>
              <p className="text-sm">Try different keywords</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="p-4 border-t border-border text-center text-xs text-muted-foreground">
        <p>Fuelogic v1.0 • Need more help? Contact support@fuelogic.in</p>
      </div>
    </div>
  );
};

export default UserGuide;
